import {
  EBucketUploadAWSRegions,
  EBucketUploadType,
  IBucketUploadSetting,
  ICommonSettings,
  IImageLibraryDto
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { ImageLibraryService } from "../../../../../services/image-library-service";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigFilePicker } from "../config-file-picker";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigBucketUploadSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IBucketUploadSetting;
  allImage?: IImageLibraryDto[];
  serviceAccountJSONFileName: string;
}

export interface IConfigBucketUploadSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingBucketUploadId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigBucketUploadSettings extends React.PureComponent<
  IConfigBucketUploadSettingsProps,
  IConfigBucketUploadSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigBucketUploadSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      allImage: [],
      serviceAccountJSONFileName: ""
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigBucketUploadSettingsProps & IWithSDProps) {
    if (prevProps.editingBucketUploadId !== this.props.editingBucketUploadId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    this.setState({ serviceAccountJSONFileName: "" });
    if (!this.props.editingBucketUploadId) return;

    const editingConfig = this.props.commonSettings.bucketUploadSettings?.find(
      (x) => x.id === this.props.editingBucketUploadId
    );

    if (!editingConfig) return;
    this.setState(
      { editingConfig, serviceAccountJSONFileName: editingConfig.googleServiceAccountKeyJson ?? "" },
      () => {
        this.formRef.current?.resetFields();
        this.formRef.current?.setFieldsValue(editingConfig);
      }
    );
  };

  existingNameValidator = async (_rule, value) => {
    const bucketUploadSetting = this.props.commonSettings?.bucketUploadSettings;
    if (!bucketUploadSetting) return;

    const isSettingAlreadyExist = bucketUploadSetting.some(
      (x) => x.name === value && x.id !== this.props.editingBucketUploadId
    );

    if (isSettingAlreadyExist) throw new Error("Bucket Upload Name. is already exist.");
  };

  onFinish = (data: any) => {
    if (!this.state.serviceAccountJSONFileName && data.type === EBucketUploadType.Google) {
      KMainFunctions.displayWarningNotification("Please choose a Service Account Key Json file");
      return;
    }
    this.saveStatus.start();
    let obj: IBucketUploadSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      type: data.type,
      encryptionKey: data.encryptionKey,
      encryptionIV: data.encryptionIV
    };

    if (obj.type === EBucketUploadType.AWS) {
      obj = {
        ...obj,
        awsBucketName: data.awsBucketName,
        awsRegion: data.awsRegion,
        awsAccessKeyId: data.awsAccessKeyId,
        awsSecretAccessKey: data.awsSecretAccessKey
      };
    } else if (obj.type === EBucketUploadType.Google) {
      obj = {
        ...obj,
        googleBucketName: data.googleBucketName,
        googleServiceAccountKeyJson: this.state.serviceAccountJSONFileName
      };
    } else if (obj.type === EBucketUploadType.Azure) {
      obj = {
        ...obj,
        azureConnectionString: data.azureConnectionString,
        azureContainerName: data.azureContainerName
      };
    } else if (obj.type === EBucketUploadType.MinIO) {
      obj = {
        ...obj,
        minioEndpoint: data.minioEndpoint,
        minioAccessKey: data.minioAccessKey,
        minioSecretKey: data.minioSecretKey,
        minioBucketName: data.minioBucketName
      };
    }
    if (this.props.editingBucketUploadId) {
      obj.id = this.props.editingBucketUploadId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "bucketUploadSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "bucketUploadSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingBucketUploadId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingBucketUploadId || "",
      configKey: "bucketUploadSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Bucket Upload setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  getImageLibrary = async () => {
    try {
      const res = await ImageLibraryService.GetAll();
      if (res && res.status === 200) {
        this.setState({ allImage: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
    }
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Bucket Upload setting"
        secondHeader="Bucket Upload setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingBucketUploadId ? this.deleteSetting : undefined}
            deleteTestId="bucketUploadSettingDeleteButton"
            submitTestId="bucketUploadSettingCreateButton"
            submitLabel={this.props.editingBucketUploadId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configBucketUploadForm"
          />
        }
      >
        <Form
          id="configBucketUploadForm"
          name="bucketUploadSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("")}>
            <ConfigField label="Name" required tooltipContentKey="">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Bucket Upload Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Bucket Upload Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="bucketUploadSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>

            <ConfigField label="Type" required tooltipContentKey={""}>
              <Form.Item name="type" rules={[{ required: true, message: "Please select your Bucket Upload Type." }]}>
                <KuikaSelectbox
                  size="md"
                  disabled={!!this.props.editingBucketUploadId}
                  data-testid="bucketUploadSettingTypeSelect"
                  options={[
                    { value: EBucketUploadType.AWS, label: "AWS", testId: "bucketUploadSettingAwsOption" },
                    { value: EBucketUploadType.Google, label: "Google", testId: "bucketUploadSettingGoogleOption" },
                    {
                      value: EBucketUploadType.Azure,
                      label: "Azure Blob Storage",
                      testId: "bucketUploadSettingAzureOption"
                    },
                    { value: EBucketUploadType.MinIO, label: "MinIO", testId: "bucketUploadSettingMinioOption" }
                  ]}
                />
              </Form.Item>
            </ConfigField>
            <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type} noStyle>
              {({ getFieldValue }) => {
                if (getFieldValue("type") === EBucketUploadType.AWS) {
                  return (
                    <>
                      <ConfigField label="Bucket Name" required tooltipContentKey="">
                        <Form.Item
                          name="awsBucketName"
                          rules={[{ required: true, message: "Please enter your AWS Bucket Name." }]}
                        >
                          <KuikaInput size="md" autoFocus />
                        </Form.Item>
                      </ConfigField>
                      <ConfigField label="Region" required tooltipContentKey={""}>
                        <Form.Item
                          name="awsRegion"
                          rules={[{ required: true, message: "Please select your AWS Bucket Upload Region." }]}
                        >
                          <KuikaSelectbox
                            size="md"
                            options={Object.entries(EBucketUploadAWSRegions).map(
                              ([key, value]: [string, string]): KuikaSelectboxOption => ({ value, label: key })
                            )}
                          />
                        </Form.Item>
                      </ConfigField>
                      <ConfigField label="Access Key Id" required tooltipContentKey="">
                        <Form.Item
                          name="awsAccessKeyId"
                          rules={[{ required: true, message: "Please enter your AWS Bucket Upload Access Key Id." }]}
                        >
                          <KuikaInput size="md" autoFocus />
                        </Form.Item>
                      </ConfigField>
                      <ConfigField label="Secret Access Key" required tooltipContentKey="">
                        <Form.Item
                          name="awsSecretAccessKey"
                          rules={[
                            { required: true, message: "Please enter your AWS Bucket Upload Secret Access Key." }
                          ]}
                        >
                          <KuikaInput size="md" autoFocus />
                        </Form.Item>
                      </ConfigField>
                    </>
                  );
                }

                if (getFieldValue("type") === EBucketUploadType.Google) {
                  return (
                    <>
                      <ConfigField label="Bucket Name" required tooltipContentKey="">
                        <Form.Item
                          name="googleBucketName"
                          rules={[{ required: true, message: "Please enter your Google Bucket Name." }]}
                        >
                          <KuikaInput size="md" autoFocus />
                        </Form.Item>
                      </ConfigField>
                      <ConfigField label="Service Account Key JSON" required tooltipContentKey="">
                        <ConfigFilePicker
                          files={this.state.allImage ?? []}
                          acceptedFileTypes=".json"
                          getImageLibrary={this.getImageLibrary}
                          selectedId={
                            this.state.allImage?.find(
                              (x: IImageLibraryDto): boolean => x.fileName === this.state.serviceAccountJSONFileName
                            )?.id
                          }
                          fileName={this.state.serviceAccountJSONFileName}
                          onSelect={(image: IImageLibraryDto): void =>
                            this.setState({ serviceAccountJSONFileName: image.fileName }, (): void => {
                              this.formRef.current?.validateFields().catch((): void => {});
                            })
                          }
                          onClear={(): void => this.setState({ serviceAccountJSONFileName: "" })}
                        />
                      </ConfigField>
                    </>
                  );
                }

                if (getFieldValue("type") === EBucketUploadType.Azure) {
                  return (
                    <>
                      <ConfigField label="Connection String" required tooltipContentKey="">
                        <Form.Item
                          name="azureConnectionString"
                          rules={[{ required: true, message: "Please enter your Azure Blob Connection String." }]}
                        >
                          <KuikaInput
                            type="textarea"
                            size="md"
                            rows={3}
                            data-testid="bucketUploadSettingAzureConnectionStringInput"
                          />
                        </Form.Item>
                      </ConfigField>
                      <ConfigField label="Container Name" required tooltipContentKey="">
                        <Form.Item
                          name="azureContainerName"
                          rules={[{ required: true, message: "Please enter your Azure Blob Container Name." }]}
                        >
                          <KuikaInput size="md" data-testid="bucketUploadSettingAzureContainerNameInput" />
                        </Form.Item>
                      </ConfigField>
                    </>
                  );
                }

                if (getFieldValue("type") === EBucketUploadType.MinIO) {
                  return (
                    <>
                      <ConfigField label="Endpoint" required tooltipContentKey="">
                        <Form.Item
                          name="minioEndpoint"
                          rules={[
                            {
                              required: true,
                              message: "Please enter your MinIO Endpoint (e.g. https://minio.example.com)."
                            }
                          ]}
                        >
                          <KuikaInput size="md" />
                        </Form.Item>
                      </ConfigField>
                      <ConfigField label="Bucket Name" required tooltipContentKey="">
                        <Form.Item
                          name="minioBucketName"
                          rules={[{ required: true, message: "Please enter your MinIO Bucket Name." }]}
                        >
                          <KuikaInput size="md" />
                        </Form.Item>
                      </ConfigField>
                      <ConfigField label="Access Key" required tooltipContentKey="">
                        <Form.Item
                          name="minioAccessKey"
                          rules={[{ required: true, message: "Please enter your MinIO Access Key." }]}
                        >
                          <KuikaInput size="md" />
                        </Form.Item>
                      </ConfigField>
                      <ConfigField label="Secret Key" required tooltipContentKey="">
                        <Form.Item
                          name="minioSecretKey"
                          rules={[{ required: true, message: "Please enter your MinIO Secret Key." }]}
                        >
                          <KuikaInput size="md" />
                        </Form.Item>
                      </ConfigField>
                    </>
                  );
                }
                return <></>;
              }}
            </Form.Item>
            <ConfigField label="Encyrption Key" tooltipContentKey="">
              <Form.Item name="encryptionKey">
                <KuikaInput size="md" autoComplete="off" type="password" revealable />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Encryption IV" tooltipContentKey="">
              <Form.Item name="encryptionIV">
                <KuikaInput size="md" autoComplete="off" type="password" revealable />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
